import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Home, MessageCircle, ChevronDown, ChevronUp, HelpCircle, Clock, CreditCard, Users, BookMarked, Settings, Lightbulb, LayoutDashboard } from 'lucide-react';
import { usePageView } from '../utils/usePageView';
import { useUser } from '../UserContext';

const FAQPage = () => {
  usePageView('faq');
  const navigate = useNavigate();
  const { user } = useUser();
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  const categories = [
    { id: 'all', label: 'All Questions', icon: <HelpCircle size={20} /> },
    { id: 'getting-started', label: 'Getting Started', icon: <Lightbulb size={20} /> },
    { id: 'lessons', label: 'Lessons & Learning', icon: <BookMarked size={20} /> },
    { id: 'technical', label: 'Technical', icon: <Settings size={20} /> },
    { id: 'account', label: 'Account & Billing', icon: <CreditCard size={20} /> }
  ];

  const faqs = [
    // Getting Started
    {
      id: 1,
      category: 'getting-started',
      question: "How do I get started with BrightReading?",
      answer: "Getting started takes about a minute. Create a free account, then from your dashboard pick the phase that matches your child's age (1-2, 2-3, 3-4, or 4-5). You'll land on Activity 1 of that phase, and from there the 1,040 scripted activities are sequenced for you in order — so you always know exactly what to do next. Want to see it first? Tap 'Try a Free Activity' on the homepage to run a full activity with no account required, and once you sign up you'll have 10 free activities before the paywall to make sure BrightReading is the right fit."
    },
    {
      id: 2,
      category: 'getting-started',
      question: "Which age group should I choose for my child?",
      answer: "Pick the phase that matches your child's current age — 1-2, 2-3, 3-4, or 4-5. If your child is ahead or needs more practice, choose by skill level instead of age; the curriculum is built so any phase is a healthy place to start. You can switch phases at any time from your dashboard with one tap, and your progress in every phase is saved separately, so exploring is risk-free."
    },
    {
      id: 3,
      category: 'getting-started',
      question: "Do I need any special materials or books?",
      answer: "No special materials needed. Almost every activity is built around things you already have at home — paper, crayons, household objects, and your voice. On the rare occasion an activity suggests a book, any age-appropriate picture book from your shelf or the local library works perfectly. BrightReading is intentionally screen-free for your child and zero-prep for you."
    },
    {
      id: 4,
      category: 'getting-started',
      question: "Do I need teaching experience to use BrightReading?",
      answer: "Not at all — BrightReading is built specifically for parents with no teaching background. Every activity gives you a word-for-word script of what to say, the exact actions to take, and a 'watch for' cue so you know what success looks like. If you can read the script aloud, you can teach it. Think of it as a literacy expert sitting next to you, walking you through each step."
    },

    // Activities & Learning
    {
      id: 5,
      category: 'lessons',
      question: "How long should each activity  take?",
      answer: "Each activity is designed to run about 10 minutes. Some days your child will stay engaged for 15-20, other days 5 minutes is plenty — both are wins. The best rule is to stop while it's still fun. Short, positive, consistent sessions build the habit (and the skill) faster than long ones, so quality always beats quantity here."
    },
    {
      id: 6,
      category: 'lessons',
      question: "How often should we do activities?",
      answer: "3-5 times a week is the sweet spot. Consistency matters more than daily practice — a steady rhythm of short sessions outperforms occasional long ones every time. Life will get in the way, and that's completely fine: your progress and streak are saved, so you can pause and pick up exactly where you left off whenever you're ready."
    },
    {
      id: 7,
      category: 'lessons',
      question: "What if my child already knows some of the content?",
      answer: "That's a great sign — it means the foundation is already solid. You have a few options: move quickly through familiar activities (still mark them complete to keep your streak going), skip ahead to a later activity in the same phase, or jump to the next phase entirely from your dashboard. The curriculum is a guide, not a cage, so adjust freely up or down to match your child."
    },
    {
      id: 8,
      category: 'lessons',
      question: "What if we miss several days or weeks?",
      answer: "Totally fine — there's no such thing as 'behind' in early learning. Vacations, illness, busy stretches are all normal, and there's no penalty for breaks. Your progress, completed activities, and current spot are saved to your account. When you come back, just open the dashboard and tap continue — you'll be right where you left off."
    },
    {
      id: 9,
      category: 'lessons',
      question: "My child won't sit still during activities. Is this normal?",
      answer: "Completely normal — young children learn through movement, not stillness. Try doing the activity wherever they are: on the floor, in the bath, on a walk, or while they're wiggling on the couch. Read while they swing, practice letter sounds while they jump, act stories out together. Meet them where they are, keep it short, and follow their energy."
    },
    {
      id: 10,
      category: 'lessons',
      question: "Can I do activities in a language other than English?",
      answer: "Yes! The foundational skills BrightReading builds — sound awareness, vocabulary, letter recognition, comprehension — transfer across every language. Feel free to deliver the scripts in your home language; the underlying activity still works. Bilingual kids especially benefit from running each activity in both languages, since the skills reinforce each other."
    },
    {
      id: 11,
      category: 'lessons',
      question: "When will my child start reading?",
      answer: "Every child develops on their own timeline. Some recognize words by age 4, others read independently at 6 or 7 — both are completely healthy. What matters is the foundation underneath: sounds, letters, blending, vocabulary, comprehension. The four BrightReading phases lay these in order, so by the time reading 'clicks,' everything it needs is already in place. Trust the process and celebrate the small wins."
    },
    {
      id: 12,
      category: 'lessons',
      question: "Is BrightReading enough, or should my child also attend preschool?",
      answer: "BrightReading is a complete early-literacy program — 1,040 scripted activities covering ages 1 through 5 — but it's the literacy piece, not the whole picture of childhood. Preschool, playgroups, library storytime, outdoor play, and unstructured time with you all add the social, motor, and emotional development kids also need. Use BrightReading as your daily literacy anchor and let real life fill in the rest."
    },
    {
      id: 13,
      category: 'lessons',
      question: "What if my child gets frustrated or upset during an activity ?",
      answer: "Stop the activity right away — calmly, no big deal. Frustration is a signal, not a failure, and learning should never feel stressful. Try again in a few hours, the next day, or skip the activity for a week and come back fresh. You can also lower the difficulty by doing half the script, modeling more, and asking less. Keeping the experience positive matters far more than finishing any single activity."
    },
    {
      id: 14,
      category: 'lessons',
      question: "Can I work with multiple children at different levels?",
      answer: "Yes. From your dashboard you can add multiple children and switch between phases with one tap, so an older sibling can be working in one phase while a younger one works in another. Each phase keeps its own progress tracking, so nothing gets mixed up. Some activities adapt nicely to siblings doing them together; others are best one-on-one — you'll get a feel for the difference quickly."
    },

    // Technical
    {
      id: 15,
      category: 'technical',
      question: "How do I track my child's progress?",
      answer: "Your dashboard is the home base. It shows your active phase, every activity you've completed, your current streak, and exactly which activity is next. After each session you tap to mark the activity complete and the dashboard updates instantly. You can scroll the activity list at any time to see how far you've come within each of the four phases."
    },
    {
      id: 16,
      category: 'technical',
      question: "Can I access BrightReading on multiple devices?",
      answer: "Yes — BrightReading runs in any modern web browser and your account syncs in real time. Start an activity on your laptop, continue on a tablet, check progress from your phone. Just log in with the same email on any device and everything is right where you left it."
    },
    {
      id: 17,
      category: 'technical',
      question: "Do I need an internet connection to use BrightReading?",
      answer: "Yes — BrightReading needs an internet connection so your activities, progress, and sync stay live across devices. Any reliable connection works fine; you don't need fast internet, just a stable one. The activities themselves are lightweight, so even slow Wi-Fi or mobile data handles them comfortably."
    },
    {
      id: 18,
      category: 'technical',
      question: "What if I encounter a technical problem?",
      answer: "First, refresh the page — that resolves the vast majority of hiccups. If it persists, head to the Contact page and send us a quick note describing what happened, and the device or browser you're using if you can. We read every message and typically respond within 24 hours."
    },

    // Account & Billing
    {
      id: 29,
      category: 'account',
      question: "Is there a free trial available?",
      answer: "Yes! You can run one full BrightReading activity right now with no account, no credit card, and no email — just tap 'Try a Free Activity' on the homepage, choose your child's age, and go. After that, creating a free account unlocks 10 total free activities before you reach the paywall, so you can get a real feel for the program (and a real start on your child's foundation) before subscribing. It's the exact same activity experience paid users get."
    },
    {
      id: 20,
      category: 'account',
      question: "How much does BrightReading cost?",
      answer: "Two simple options: $10.99/month, or $95.88/year (which works out to $7.99/month — a 27% saving over monthly). Both plans unlock all 1,040 activities, all four phases (ages 1-5), full progress tracking, achievement badges, and you can cancel anytime. No hidden fees, no per-child upcharges."
    },
    {
      id: 21,
      category: 'account',
      question: "Can I cancel my subscription anytime?",
      answer: "Yes — anytime, with no fees and no hoops. You can cancel from your profile in a couple of clicks. You'll keep full access through the end of your current billing period, and your progress data stays saved on your account in case you ever come back."
    },
    {
      id: 22,
      category: 'account',
      question: "How do I change my account settings or password?",
      answer: "Open your profile from the dashboard to update your email, child information, and preferences. Most changes save automatically. To reset your password, use the 'Forgot password' link on the login screen — you'll get a secure reset email within a minute or two."
    },
    {
      id: 23,
      category: 'account',
      question: "What happens to my data if I cancel?",
      answer: "Your account stays put. Your progress, streaks, child profiles, and history are all preserved, so if you reactivate later everything is right where you left off. Per our privacy policy, accounts that stay inactive for an extended period may eventually be removed — but reactivating at any time keeps your data intact."
    },
    {
      id: 24,
      category: 'account',
      question: "Is my child's information secure?",
      answer: "Absolutely. BrightReading is COPPA-compliant, we never sell or share your child's information with third parties, and we collect only what's needed to run the program. Data is stored securely and encrypted in transit. Read the full Privacy Policy for the complete details on how we handle, store, and protect your family's data — and your rights to access or delete it at any time."
    }
  ];

  const filteredFaqs = selectedCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === selectedCategory);

  return (
<div className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-blue-50 via-purple-50 to-pink-50">
      {/* Navigation */}
      <nav className="bg-white/80 shadow-sm p-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
            <img src="/assets/brlogo2.webp" alt="BrightReading Logo" className="h-8 sm:h-10 w-auto object-contain flex-shrink-0" />
            <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent whitespace-nowrap">
              BrightReading
            </h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            {user && (
              <button
                onClick={() => { navigate('/dashboard'); window.scrollTo(0, 0); }}
                className="text-purple-600 hover:bg-purple-50 p-2 rounded-lg transition"
                title="Dashboard"
              >
                <LayoutDashboard size={24} />
              </button>
            )}
<button
              onClick={() => {
                navigate('/');
                window.scrollTo(0, 0);
              }}
              className="text-purple-600 hover:bg-purple-50 p-2 rounded-lg transition"
            >
              <Home size={24} />
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-6">
            <MessageCircle className="text-white" size={40} />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Find answers to common questions about BrightReading
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition ${
                  selectedCategory === cat.id
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-50 shadow'
                }`}
              >
                {cat.icon}
                <span>{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* FAQ List */}
        <div className="space-y-4 mb-16">
          {filteredFaqs.map((faq) => (
            <div key={faq.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <button
                onClick={() => toggleFaq(faq.id)}
                className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition"
              >
                <div className="flex items-start gap-4 flex-1">
                  <HelpCircle className="text-purple-600 flex-shrink-0 mt-1" size={24} />
                  <h3 className="text-lg font-bold text-gray-900 pr-4">{faq.question}</h3>
                </div>
                <div className="text-gray-400 ml-4 flex-shrink-0">
                  {expandedFaq === faq.id ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                </div>
              </button>
              {expandedFaq === faq.id && (
                <div className="px-6 pb-6 pl-16">
                  <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Still Have Questions */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-5 sm:p-8 md:p-12 text-center text-white">
          <MessageCircle className="mx-auto mb-4" size={48} />
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-xl mb-8 opacity-95 max-w-2xl mx-auto">
            We're here to help! Check out our Parent Guide for tips and best practices, or reach out to our support team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                navigate('/parent-guide');
                window.scrollTo(0, 0);
              }}
              className="bg-white text-purple-600 px-8 py-3 rounded-full font-bold text-lg hover:bg-gray-50 transition shadow-lg"
            >
              View Parent Guide
            </button>
           <button
              onClick={() => {
                navigate('/contact');
                window.scrollTo(0, 0);
              }}
              className="bg-purple-700 text-white px-8 py-3 rounded-full font-bold text-lg hover:bg-purple-800 transition border-2 border-white"
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;